import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Train, Cigarette, Heart, Brain } from 'lucide-react';
import CTA from '../../components/CTA';

const BangorQuitSmoking: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Quit Smoking Hypnotherapy Bangor | Health-Focused Smoking Cessation</title>
        <meta
          name="description"
          content="Health-focused quit smoking hypnotherapy serving Bangor from a south Belfast clinic on the Ormeau Road. Intensive 2-hour session. Longevity motivation. 60-80% success rates."
        />
        <meta name="keywords" content="quit smoking Bangor, stop smoking Bangor, health-focused smoking cessation Bangor, quit vaping Bangor, longevity hypnotherapy" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/bangor-quit-smoking/" />
        <meta property="og:title" content="Quit Smoking Hypnotherapy Bangor | Health-Focused Smoking Cessation" />
        <meta property="og:description" content="Health-focused quit smoking hypnotherapy serving Bangor. Longevity motivation. Intensive 2-hour session." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Smoking Cessation Bangor",
            "description": "CNHC registered hypnotherapy for smoking cessation, serving Bangor from Belfast clinic. Intensive 2-hour session with lifetime support and money-back guarantee. Health longevity focus for retirement demographic.",
            "url": "https://www.sharonclare-hypnotherapy.com/bangor-quit-smoking/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Smoking Cessation Hypnotherapy",
            "areaServed": {
              "@type": "City",
              "name": "Bangor",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic (Belfast Wellness Clinic, Ormeau Road)",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "addressRegion": "County Antrim",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Does quit smoking hypnotherapy work for older adults in Bangor?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Hypnotherapy is highly effective for older adults and long-term smokers who want to quit. Many Bangor clients have smoked for 30, 40, or even 50 years and successfully quit after the intensive session. The approach works regardless of how long you've been smoking - it's about rewiring the subconscious habits that control your behavior now, not about undoing decades of history. For retirees and older adults, the health longevity motivation is often particularly strong."
                }
              },
              {
                "@type": "Question",
                "name": "Can I quit smoking to reduce chronic disease risk?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, and the health benefits begin almost immediately. Within 20 minutes of quitting, your heart rate and blood pressure drop. Within 48 hours, nerve endings start regrowing and your sense of taste improves. Within 3 months, lung function improves. Within 1 year, heart disease risk drops by 50%. Even if you've smoked for decades, quitting now significantly reduces your risk of heart disease, stroke, cancer, and COPD. For Bangor clients focused on health longevity and quality retirement years, these benefits make quitting one of the most impactful health decisions you can make."
                }
              },
              {
                "@type": "Question",
                "name": "How far is the Belfast clinic from Bangor?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The Belfast clinic is on the Ormeau Road in south Belfast, about 10 minutes from the city centre and roughly half an hour by car from Bangor town centre. You can also take the train on the Belfast-Bangor line into the city, then a Metro bus along the Ormeau Road corridor or a short taxi. For Bangor residents, it is an easily accessible location without a long journey. Many Bangor clients appreciate the proximity - close enough for easy access, but far enough to feel like a dedicated investment in your health."
                }
              },
              {
                "@type": "Question",
                "name": "What if I have concerns about smoking with grandchildren around?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "This is one of the most powerful motivations for quitting. Many Bangor clients are driven by the desire to be healthy and present for grandchildren - attending sports days, playing actively, being around for important milestones. Secondhand smoke affects children's health, and smoking models behavior that children may copy. The intensive quit smoking session specifically addresses these family motivations, helping you connect with why being a non-smoker matters for your role as a grandparent. This emotional connection strengthens your commitment to quitting."
                }
              },
              {
                "@type": "Question",
                "name": "Can I quit smoking if I've tried and failed many times before?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. Most successful quitters tried multiple times before they succeeded permanently. Previous attempts aren't failures - they're learning experiences that help you understand what doesn't work. The key difference with hypnotherapy is that it addresses the subconscious habits and psychological patterns that previous methods (willpower, patches, gum) couldn't reach. Many Bangor clients who felt they'd 'failed' at quitting in the past find that hypnotherapy finally gives them the tool they needed to become permanent non-smokers."
                }
              },
              {
                "@type": "Question",
                "name": "Will quitting smoking interact with my medications?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy itself doesn't interact with medications - it's a psychological technique, not a pharmaceutical intervention. However, quitting smoking can affect how your body processes certain medications, particularly blood thinners, blood pressure medications, and diabetes medications. This is actually a positive thing (smoking interferes with many medications), but it means you should inform your GP when you quit so they can monitor and adjust dosages if needed. For Bangor clients with chronic conditions, quitting smoking often improves medication effectiveness and overall health management."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Health-Focused Quit Smoking Hypnotherapy for Bangor
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Quit smoking for health longevity and quality retirement years
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-2">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">South Belfast clinic</span> on the Ormeau Road • <span className="font-semibold">around 30 min drive</span> from Bangor
                </p>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <Train className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Direct trains</span> on the Belfast-Bangor line into the city
                </p>
              </div>
              <p className="text-gray-600 mt-2">Convenient access • Online sessions also available</p>
            </div>
            <div className="bg-white rounded-xl p-6 mb-8 max-w-2xl mx-auto">
              <div className="flex items-start space-x-4">
                <Heart className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div className="text-left">
                  <p className="text-lg font-semibold text-gray-900 mb-2">Invest in Your Health Longevity</p>
                  <p className="text-gray-700">
                    Intensive 2-hour session with lifetime support + money-back guarantee
                  </p>
                  <p className="text-accent-700 font-medium mt-1">Be healthy and present for grandchildren • Reduce chronic disease risk • Enjoy quality retirement</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Quit Smoking for Health Longevity in Bangor
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're living in Bangor and ready to quit smoking for your health, professional support is closer than you think. I serve Bangor clients from my Belfast clinic on the Ormeau Road in south Belfast, about 10 minutes from the city centre. The drive in from Bangor takes roughly half an hour, or you can take the train on the Belfast-Bangor line into the city and finish with a Metro bus along the Ormeau Road corridor or a short taxi. For Bangor's health-conscious demographic, this proximity makes specialist smoking cessation easily accessible.
              </p>
              <p>
                You already know smoking is killing you. You've read the warnings, seen the statistics, possibly even watched loved ones suffer from smoking-related illnesses. But knowing the risks hasn't been enough to make you quit, because smoking isn't about logic - it's about deeply ingrained subconscious habits that control your behavior despite what your rational mind knows.
              </p>
              <p>
                Perhaps you're approaching retirement or already retired, and you want to enjoy these years in good health. Maybe you have grandchildren and you want to be active and present in their lives - attending school events, playing with them, being around for their milestones. Or perhaps you're experiencing health concerns - breathlessness, chronic cough, high blood pressure, early signs of COPD - and your GP has urged you to quit. Whatever your motivation, the time to quit is now.
              </p>
              <p>
                Here's the good news: it's never too late to quit smoking. Even if you've smoked for 30, 40, or 50 years, your body begins healing within hours of your last cigarette. Within 1 year of quitting, your risk of heart disease drops by 50%. Within 5 years, stroke risk returns to that of a non-smoker. Within 10 years, lung cancer risk drops significantly. The health benefits are substantial regardless of your age or how long you've smoked.
              </p>
              <p>
                Solution-focused hypnotherapy works by rewiring the subconscious habits that keep you smoking. Instead of relying on willpower (which depletes throughout the day), we work with your brain's autopilot patterns to make you naturally become a non-smoker. The intensive 2-hour session includes lifetime support and a money-back guarantee. For Bangor clients focused on health longevity and quality of life, this investment in your health is one of the most impactful decisions you can make.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Health Longevity Focus */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              The Health Benefits of Quitting Smoking
            </h2>
            <div className="space-y-8">
              <div className="bg-white rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <Heart className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      Timeline of Health Recovery
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Your body begins healing remarkably quickly after quitting smoking. Understanding this timeline can strengthen your motivation:
                    </p>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>20 minutes:</strong> Heart rate and blood pressure drop to normal levels</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>12 hours:</strong> Carbon monoxide levels in blood return to normal, oxygen levels increase</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>48 hours:</strong> Nerve endings begin regrowing, sense of taste and smell improve dramatically</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>2-3 weeks:</strong> Lung function improves, breathing becomes easier, chronic cough begins to clear</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>3 months:</strong> Circulation improves significantly, walking and exercise become easier</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>1 year:</strong> Risk of heart disease drops by 50% compared to when you smoked</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>5 years:</strong> Stroke risk returns to that of a non-smoker</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>10 years:</strong> Lung cancer risk drops to about half that of a continuing smoker</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <Brain className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      Quality of Life Improvements
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Beyond disease risk reduction, quitting smoking dramatically improves your day-to-day quality of life:
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Physical fitness:</strong> Breathlessness reduces, stamina improves, you can walk coastal paths and play with grandchildren without getting winded</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Appearance:</strong> Skin tone improves, teeth become whiter, you no longer smell of smoke</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Taste and smell:</strong> Food tastes better, you can enjoy coastal air and garden scents fully</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Sleep quality:</strong> Better oxygen levels lead to deeper, more restorative sleep</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Independence:</strong> No longer planning activities around where you can smoke, freedom from addiction</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Peace of mind:</strong> No longer worrying about when you last smoked or when you can smoke next</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <Heart className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      Being Present for Grandchildren
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Many Bangor clients are motivated by their role as grandparents. Quitting smoking means:
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Physical capability:</strong> Playing actively with grandchildren without breathlessness or fatigue</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Longevity:</strong> Increasing your chances of being around for their major life events - graduations, weddings, their own children</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Protection:</strong> Eliminating secondhand smoke exposure that could affect grandchildren's health</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Role modeling:</strong> Showing grandchildren healthy behaviors and that it's never too late to change</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Smell:</strong> Hugs that don't smell of cigarettes, no more guilt about exposing them to smoke</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              How the Intensive Quit Smoking Session Works
            </h2>
            <div className="space-y-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Success Regardless of How Long You've Smoked
                </h3>
                <p className="text-gray-700 mb-4">
                  Many Bangor clients worry that because they've smoked for 30, 40, or 50 years, it's too late to quit successfully. This isn't true. Hypnotherapy works by changing your current subconscious patterns - it's not about undoing decades of history, it's about rewiring the habits that control your smoking behavior right now.
                </p>
                <p className="text-gray-700">
                  The intensive 2-hour session addresses the psychological addiction that keeps you smoking despite wanting to quit. We identify your specific smoking triggers (morning coffee, stress, after meals, social situations), reframe your relationship with cigarettes from "giving up" to "gaining freedom," and use deep hypnotherapy to rewire the automatic patterns in your basal ganglia. You also receive lifetime support - if you ever feel tempted to smoke again, you can return for a free top-up session.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Money-Back Guarantee Removes the Risk
                </h3>
                <p className="text-gray-700 mb-4">
                  The session comes with a straightforward money-back guarantee. If you attend your intensive session, follow the program recommendations, and return within 3 months still smoking, you'll receive a full refund. No hidden conditions.
                </p>
                <p className="text-gray-700">
                  This guarantee is possible because the majority of genuinely motivated clients succeed. When your motivation is strong - health concerns, grandchildren, quality of retirement years - and you combine that with the hypnotherapy approach that addresses subconscious habits, success rates are 60-80%. The guarantee simply removes any financial risk from making this investment in your health.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Bangor Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I'd smoked for 45 years and honestly didn't think I could quit. My grandchildren were my motivation - I wanted to be around for them and not smell of smoke when they hugged me. One session with Sharon and I haven't smoked in 7 months. I can play with them at the park now without getting breathless. Worth every penny."
                </p>
                <p className="font-semibold text-gray-900">R.T., Bangor - Grandparent motivation, 45-year habit</p>
              </div>

              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "My GP urged me to quit because of high blood pressure and early COPD. I'd tried willpower so many times and failed. Sharon's approach was completely different - she worked with my subconscious rather than relying on willpower. Five months smoke-free and my breathing has improved dramatically. The coastal walks I love are actually enjoyable again."
                </p>
                <p className="font-semibold text-gray-900">P.M., Groomsport - Health longevity motivation</p>
              </div>

              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Retired and wanting to enjoy these years in good health, not dealing with smoking-related illness. The train from Bangor to Belfast made it so convenient. Sharon focused on what I was gaining (health, longevity, freedom) rather than what I was 'giving up.' Four months smoke-free and I feel 10 years younger."
                </p>
                <p className="font-semibold text-gray-900">M.K., Bangor - Retirement health focus</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Bangor Quit Smoking
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does quit smoking hypnotherapy work for older adults in Bangor?
                </h3>
                <p className="text-gray-700">
                  Absolutely. Hypnotherapy is highly effective for older adults and long-term smokers who want to quit. Many Bangor clients have smoked for 30, 40, or even 50 years and successfully quit after the intensive session. The approach works regardless of how long you've been smoking - it's about rewiring the subconscious habits that control your behavior now, not about undoing decades of history. For retirees and older adults, the health longevity motivation is often particularly strong.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I quit smoking to reduce chronic disease risk?
                </h3>
                <p className="text-gray-700">
                  Yes, and the health benefits begin almost immediately. Within 20 minutes of quitting, your heart rate and blood pressure drop. Within 48 hours, nerve endings start regrowing and your sense of taste improves. Within 3 months, lung function improves. Within 1 year, heart disease risk drops by 50%. Even if you've smoked for decades, quitting now significantly reduces your risk of heart disease, stroke, cancer, and COPD. For Bangor clients focused on health longevity and quality retirement years, these benefits make quitting one of the most impactful health decisions you can make.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is the Belfast clinic from Bangor?
                </h3>
                <p className="text-gray-700">
                  The Belfast clinic is on the Ormeau Road in south Belfast, about 10 minutes from the city centre and roughly half an hour by car from Bangor town centre. You can also take the train on the Belfast-Bangor line into the city, then a Metro bus along the Ormeau Road corridor or a short taxi. For Bangor residents, it is an easily accessible location without a long journey. Many Bangor clients appreciate the proximity - close enough for easy access, but far enough to feel like a dedicated investment in your health.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What if I have concerns about smoking with grandchildren around?
                </h3>
                <p className="text-gray-700">
                  This is one of the most powerful motivations for quitting. Many Bangor clients are driven by the desire to be healthy and present for grandchildren - attending sports days, playing actively, being around for important milestones. Secondhand smoke affects children's health, and smoking models behavior that children may copy. The intensive quit smoking session specifically addresses these family motivations, helping you connect with why being a non-smoker matters for your role as a grandparent. This emotional connection strengthens your commitment to quitting.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I quit smoking if I've tried and failed many times before?
                </h3>
                <p className="text-gray-700">
                  Yes. Most successful quitters tried multiple times before they succeeded permanently. Previous attempts aren't failures - they're learning experiences that help you understand what doesn't work. The key difference with hypnotherapy is that it addresses the subconscious habits and psychological patterns that previous methods (willpower, patches, gum) couldn't reach. Many Bangor clients who felt they'd 'failed' at quitting in the past find that hypnotherapy finally gives them the tool they needed to become permanent non-smokers.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Will quitting smoking interact with my medications?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy itself doesn't interact with medications - it's a psychological technique, not a pharmaceutical intervention. However, quitting smoking can affect how your body processes certain medications, particularly blood thinners, blood pressure medications, and diabetes medications. This is actually a positive thing (smoking interferes with many medications), but it means you should inform your GP when you quit so they can monitor and adjust dosages if needed. For Bangor clients with chronic conditions, quitting smoking often improves medication effectiveness and overall health management.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default BangorQuitSmoking;
